import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Play } from "lucide-react";
import Newsletter from "@/components/Newsletter";
import { FadeIn, MaskedLine, ChapterLabel } from "@/components/Reveal";
import { BOOKS, CHARACTERS, YOUTUBE_URL, SKOOL_URL, BOOKS_BY_URL } from "@/data/content";
import useSEO from "@/hooks/useSEO";

export default function BookDetail() {
  const { id } = useParams();
  const book = BOOKS.find((b) => b.id === id);
  useSEO(
    book ? book.seoTitle : "Stories | The Cap Chronicles",
    book ? book.seoDesc : "Illustrated picture books and read-aloud stories for kids ages 4-8 from The Cap Chronicles."
  );

  if (!book) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-48 text-center">
        <h1 className="font-serif text-4xl tracking-tight">This page hasn't been written yet</h1>
        <Link to="/stories" data-testid="book-not-found-back" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest">
          <ArrowLeft size={15} /> Back to the bookshelf
        </Link>
      </main>
    );
  }

  const idx = BOOKS.indexOf(book);
  const next = BOOKS[(idx + 1) % BOOKS.length];
  const cast = CHARACTERS.filter((c) => c.from && c.from.startsWith(book.title));

  return (
    <main>
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-36 md:px-12 md:pt-44" data-testid="book-hero">
        <FadeIn>
          <Link to="/stories" data-testid="book-back-link" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-charcoal/50 transition-colors hover:text-forest">
            <ArrowLeft size={14} /> The Bookshelf
          </Link>
        </FadeIn>
        <div className="mt-10 grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <FadeIn className="relative mx-auto w-full max-w-md">
            <div className="absolute left-1/2 top-1/2 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/20 blur-3xl" />
            <img src={book.cover} alt={`${book.title} cover art`} className="relative w-full rotate-[-2deg] rounded-3xl object-cover shadow-[0_40px_80px_-30px_rgba(34,34,34,0.4)]" />
          </FadeIn>
          <div>
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky">{book.chapter}</p>
            </FadeIn>
            <h1 className="mt-4 font-serif text-5xl leading-[1.05] tracking-tight md:text-6xl" data-testid="book-title">
              <MaskedLine delay={0.15}>{book.title}</MaskedLine>
            </h1>
            <FadeIn delay={0.3}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-charcoal/70 md:text-lg">{book.description}</p>
              {book.storyLink && (
                <a href={book.storyLink} target="_blank" rel="noopener noreferrer" data-testid="book-story-link" className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest transition-colors hover:text-charcoal">
                  Learn the story behind the book <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                {book.themes.map((t) => (
                  <span key={t} className="rounded-full bg-forest/10 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-forest">{t}</span>
                ))}
              </div>
              {book.price ? (
                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <span className="font-serif text-3xl italic text-forest" data-testid="book-price">{book.price}</span>
                  <a href={BOOKS_BY_URL} target="_blank" rel="noopener noreferrer" data-testid="book-order-button" className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-gold px-10 text-sm font-bold uppercase tracking-widest text-charcoal shadow-lg transition-transform duration-300 hover:scale-[1.04]">
                    Order the Book <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              ) : (
                <p className="mt-6 font-serif text-lg italic text-charcoal/60">Coming to bookshelves soon.</p>
              )}
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-16 md:px-12" data-testid="readaloud-section">
        <ChapterLabel num="01" title="Watch the Read-Aloud" />
        <FadeIn delay={0.1}>
          {book.videoId ? (
            <div className="overflow-hidden rounded-3xl shadow-2xl">
              <iframe
                className="aspect-video w-full"
                src={`https://www.youtube.com/embed/${book.videoId}`}
                title={`${book.title} read-aloud`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="readaloud-player"
              />
            </div>
          ) : (
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="readaloud-youtube-link" className="group relative block overflow-hidden rounded-3xl shadow-xl">
              <img src={book.cover} alt="" className="aspect-video w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-charcoal/45" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cream/90 text-charcoal shadow-xl transition-transform duration-300 group-hover:scale-110">
                  <Play size={22} fill="currentColor" />
                </span>
                <p className="font-serif text-2xl italic text-cream md:text-3xl">Read-aloud premiering soon</p>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/75">Watch on YouTube</p>
              </div>
            </a>
          )}
        </FadeIn>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 md:px-12" data-testid="book-characters">
        <ChapterLabel num="02" title="The Characters" />
        {cast.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cast.map((c, i) => (
              <FadeIn key={c.id} delay={i * 0.08}>
                <Link to="/characters" data-testid={`book-character-${c.id}`} className="group block overflow-hidden rounded-3xl bg-white shadow-[0_20px_40px_-20px_rgba(47,107,60,0.15)] transition-shadow duration-500 hover:shadow-[0_30px_60px_-20px_rgba(47,107,60,0.3)]">
                  <div className="overflow-hidden">
                    <img src={c.img} alt={c.name} className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">{c.role}</p>
                    <h2 className="mt-1 font-serif text-2xl">{c.name}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-charcoal/60">{c.short}</p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        ) : (
          <FadeIn>
            <p className="font-serif text-2xl italic text-charcoal/50">The characters of this story are arriving soon…</p>
          </FadeIn>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12">
        <FadeIn>
          <Link to={`/stories/${next.id}`} data-testid="next-book-link" className="group flex items-center justify-between gap-6 rounded-3xl border border-charcoal/10 bg-white p-8 transition-shadow duration-500 hover:shadow-[0_30px_60px_-25px_rgba(47,107,60,0.25)]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal/50">Next on the shelf</p>
              <p className="mt-2 font-serif text-2xl tracking-tight transition-colors group-hover:text-forest md:text-3xl">{next.title}</p>
            </div>
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-charcoal/15 text-charcoal transition-all duration-300 group-hover:border-forest group-hover:bg-forest group-hover:text-cream">
              <ArrowRight size={18} />
            </span>
          </Link>
        </FadeIn>
      </section>

      <Newsletter />
    </main>
  );
}
